import { useEffect, useId, useRef, useState } from 'react';
import { IconCheck, IconChevronRight } from '@tabler/icons-react';
import type { KeyboardEvent } from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  options: ReadonlyArray<SelectOption>;
  placeholder?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
}

const triggerClass =
  'flex h-14 w-full items-center justify-between gap-2 rounded-lg border border-border-strong bg-surface px-4 text-left text-base text-ink transition-colors focus:border-accent focus:outline-none aria-[invalid=true]:border-red-600';

/**
 * Accessible custom select. Unlike a native `<select>`, the list is an
 * absolutely-positioned overlay anchored to the field, so it opens in place,
 * animates, and never shifts surrounding content. Keyboard support: Enter/Space
 * to open, arrows/Home/End to move, Enter to pick, Escape to close.
 */
export function Select({
  id,
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedby,
  'aria-labelledby': ariaLabelledby,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const listId = `${id ?? reactId}-listbox`;
  const optionId = (index: number) => `${listId}-opt-${index}`;

  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [open, onBlur]);

  // Keep the highlighted option visible while navigating with the keyboard.
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const activeEl = rootRef.current?.querySelector(`#${CSS.escape(optionId(activeIndex))}`);
    activeEl?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  const openMenu = () => {
    const current = options.findIndex((option) => option.value === value);
    setActiveIndex(current >= 0 ? current : 0);
    setOpen(true);
  };

  const closeMenu = () => {
    setOpen(false);
    onBlur?.();
  };

  const selectAt = (index: number) => {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    setOpen(false);
    onBlur?.();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (open) setActiveIndex((index) => Math.min(options.length - 1, index + 1));
        else openMenu();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (open) setActiveIndex((index) => Math.max(0, index - 1));
        else openMenu();
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) selectAt(activeIndex);
        else openMenu();
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          closeMenu();
        }
        break;
      case 'Tab':
        if (open) closeMenu();
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        id={id}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedby}
        aria-labelledby={ariaLabelledby}
        className={triggerClass}
      >
        <span className={selected ? 'truncate' : 'text-text-placeholder truncate'}>
          {selected ? selected.label : placeholder}
        </span>
        <IconChevronRight
          className={`text-text-placeholder size-5 shrink-0 transition-transform duration-200 ${
            open ? 'rotate-90' : ''
          }`}
          aria-hidden
        />
      </button>

      <ul
        id={listId}
        role="listbox"
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        tabIndex={-1}
        className={`border-border bg-surface absolute inset-x-0 top-full z-20 mt-2 max-h-60 overflow-y-auto rounded-lg border p-1 shadow-[0px_8px_24px_0px_rgba(13,12,12,0.12)] transition-all duration-150 ease-out motion-reduce:transition-none ${
          open ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-1 opacity-0'
        }`}
      >
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isActive = index === activeIndex;
          return (
            <li
              key={option.value}
              id={optionId(index)}
              role="option"
              aria-selected={isSelected}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectAt(index)}
              className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2.5 text-base ${
                isActive ? 'bg-accent-subtle text-accent' : 'text-ink'
              } ${isSelected ? 'font-medium' : ''}`}
            >
              <span className="truncate">{option.label}</span>
              {isSelected ? (
                <IconCheck className="text-accent size-4 shrink-0" aria-hidden />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
